import React, { useMemo } from "react";
import cn from "classnames";

import { useTranslation } from "@litres-libs/i18n";
import { SearchResponseItemTypesEnum } from "@litres-libs/types";
import { getPersonRole } from "@litres/utils";
import { Avatar, AvatarType } from "@litres/ui-kit/avatar";
import { AdaptiveCover } from "@litres/ui-kit/cover";

import { SmartLink } from "@litres-modules/smart-link";
import {
  formatItemType,
  getDividedNumber,
  trackerSpotlightClickItem,
} from "../utils";
import { getBookImageURL } from "../../../utils/helpers";
import { Image } from "../../Image/Image";
import TagIcon from "../../../../static/icons/tag.svg";
import GenreIcon from "../../../../static/icons/genre.svg";
import EmptyPublisher from "../../../../static/icons/emptyPublisher.svg";

import type { ISmallSearchArt, UnsoldItemProps } from "../types";

import styles from "./UnsoldItem.module.scss";

export const UnsoldItem = (props: UnsoldItemProps): JSX.Element => {
  const {
    url,
    itemType,
    role,
    title,
    id,
    arts,
    isUserAnonymous,
    externalRef,
    searchQuery,
    smallLogo,
    saveSearchQuery,
    eventData,
    isDesktop = true,
    image_url,
    arts_count,
  } = props;

  const { t } = useTranslation();

  const handleSearchItemClick = (): void => {
    if (!isUserAnonymous && searchQuery) {
      saveSearchQuery(searchQuery);
    }
  };

  const getItemImage = useMemo(() => {
    if (itemType === SearchResponseItemTypesEnum.tag) {
      return (
        <Image
          src={TagIcon}
          fallbackSrc={TagIcon}
          width={56}
          height={56}
          alt="tag-icon"
        />
      );
    }
    if (itemType === SearchResponseItemTypesEnum.genre) {
      return (
        <Image
          src={GenreIcon}
          fallbackSrc={GenreIcon}
          width={56}
          height={56}
          alt="genre-icon"
        />
      );
    }
    if (itemType === SearchResponseItemTypesEnum.person) {
      return (
        <Avatar
          id={Number(id)}
          imageURL={image_url}
          type={AvatarType.Author}
          containerSize={56}
          size={100}
          className={styles.removeMargin}
        />
      );
    }
    if (itemType === SearchResponseItemTypesEnum.publisher) {
      return smallLogo ? (
        <Image
          src={image_url ? `https://www.litres.ru${image_url}` : ""}
          className={styles.publishingCover}
          fallbackSrc={EmptyPublisher}
          alt={title}
        />
      ) : (
        <Image
          src={EmptyPublisher}
          fallbackSrc={EmptyPublisher}
          width={56}
          height={56}
          alt="empty-publisher-icon"
        />
      );
    }
    // остались серии и подборки: отображаем 1-3 обложки книг
    return (
      <span className={styles.multipleCover}>
        {arts?.length === 1 ? (
          <AdaptiveCover
            alt={arts[0].title}
            artId={arts[0].id}
            coverSizes={{
              desktopNormal: 70,
              desktopHighDensity: 120,
              mobileNormal: 70,
              mobileHighDensity: 120,
              byHeight: true,
            }}
            unloaderSize={20}
          />
        ) : (
          arts?.slice(0, 3).map((art: ISmallSearchArt, index: number) => {
            const { cover_ratio: coverRatio, id } = art;

            return (
              <span
                className={cn(styles.childCover, {
                  [styles.firstCover]: index === 0 && arts.length > 1,
                  [styles.secondCover]: index === 1,
                  [styles.thirdCover]:
                    (index === 2 && arts.length > 1) ||
                    (index === 0 && arts.length === 1),
                })}
                key={id}
              >
                <img
                  src={getBookImageURL(id)}
                  style={{
                    width: 30,
                    height: Math.round(30 / Number(coverRatio)),
                  }}
                  alt={title}
                />
              </span>
            );
          })
        )}
      </span>
    );
  }, []);

  return (
    <li
      className={cn(styles.unsoldItemWrapperAB, {
        [styles.mobileWrapper]: !isDesktop,
      })}
      key={url}
      onClick={(e: React.MouseEvent): void => {
        handleSearchItemClick();
        trackerSpotlightClickItem(eventData, e, url);
      }}
      ref={externalRef}
      role="listitem"
      aria-label={t("Перейти")}
    >
      <SmartLink className={styles.item} href={url} aria-label={title}>
        <div className={styles.coverWrapper}>{getItemImage}</div>
        <div className={styles.details}>
          <span className={styles.title}>
            <span>{title}</span>
          </span>
          <div>
            <span className={styles.type}>
              {role
                ? t(getPersonRole(role as string))
                : t(formatItemType(itemType))}
              {arts_count &&
                arts_count > 0 &&
                `, ${getDividedNumber(
                  t("{count} книга", { count: arts_count })
                )}`}
            </span>
          </div>
        </div>
      </SmartLink>
    </li>
  );
};
